import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { usePolicies } from '../context/PoliciesContext';
import { 
  ArrowLeft, 
  Calendar, 
  DollarSign, 
  TrendingUp, 
  FileText, 
  Clock,
  Edit3
} from 'lucide-react';
import { format, parseISO, addMonths, addYears, differenceInMonths, differenceInYears, isValid } from 'date-fns';

const PolicyDetailPage = () => {
  const { policyNumber } = useParams();
  const navigate = useNavigate();
  const { policies } = usePolicies();
  const [activeTab, setActiveTab] = useState('overview');
  const [viewMode, setViewMode] = useState('monthly');

  const policy = policies.find(p => p.policy_number === policyNumber);

  useEffect(() => {
    if (!policy) {
      navigate('/policies');
    }
  }, [policy, navigate]);

  if (!policy) {
    return (
      <div className="min-h-screen bg-quickbooks-gray flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🔍</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Policy Not Found</h1>
          <p className="text-gray-600 mb-4">The requested policy could not be found.</p>
          <Link 
            to="/policies" 
            className="inline-flex items-center px-4 py-2 bg-enpact-red text-white rounded-lg hover:bg-enpact-red-dark transition-colors duration-200"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Policies
          </Link>
        </div>
      </div>
    );
  }

  const generatePaymentSchedule = (policy) => {
    const schedule = [];
    const startDate = parseISO(policy.effective_start_date);
    
    // Validate start date
    if (!isValid(startDate)) {
      console.warn('Invalid start date:', policy.effective_start_date);
      return schedule;
    }
    
    const endDate = policy.effective_end_date ? parseISO(policy.effective_end_date) : null;
    
    // Validate end date if it exists
    if (endDate && !isValid(endDate)) {
      console.warn('Invalid end date:', policy.effective_end_date);
    }
    
    let currentDate = startDate;
    let paymentCount = 0;
    
    while (paymentCount < policy.total_period && (!endDate || currentDate <= endDate)) {
      const payment = {
        date: currentDate,
        amount: policy.premium_amount,
        period: paymentCount + 1,
        policy_number: policy.policy_number,
        currency: policy.currency
      };
      
      schedule.push(payment);
      
      if (policy.premium_frequency === 'monthly') {
        currentDate = addMonths(currentDate, 1);
      } else {
        currentDate = addYears(currentDate, 1);
      }
      
      paymentCount++;
    }
    
    return schedule;
  };

  const formatCurrency = (amount, currency) => {
    const locale = currency === 'JPY' ? 'ja-JP' : currency === 'EUR' ? 'de-DE' : 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: currency === 'JPY' ? 0 : 2,
      maximumFractionDigits: currency === 'JPY' ? 0 : 2,
    }).format(amount);
  };

  const formatDate = (date) => {
    try {
      if (!date) return 'N/A';
      
      const parsedDate = typeof date === 'string' ? parseISO(date) : date;
      
      if (!isValid(parsedDate)) {
        console.warn('Invalid date value:', date);
        return 'Invalid Date';
      }
      
      return format(parsedDate, 'MMMM dd, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error, date);
      return 'Invalid Date';
    }
  };

  const calculatePolicyMetrics = () => {
    const schedule = generatePaymentSchedule(policy);
    const totalValue = policy.premium_amount * policy.total_period;
    
    let duration = 0;
    
    try {
      const startDate = parseISO(policy.effective_start_date);
      if (isValid(startDate)) {
        const endDate = policy.effective_end_date ? parseISO(policy.effective_end_date) : null;
        
        if (endDate && isValid(endDate)) {
          if (policy.premium_frequency === 'monthly') {
            duration = differenceInMonths(endDate, startDate) + 1;
          } else {
            duration = differenceInYears(endDate, startDate) + 1;
          }
        } else {
          // Calculate based on total periods if no valid end date
          if (policy.premium_frequency === 'monthly') {
            duration = policy.total_period;
          } else {
            duration = policy.total_period;
          }
        }
      }
    } catch (error) {
      console.error('Error calculating duration:', error);
      duration = policy.total_period;
    }

    return {
      totalValue,
      duration,
      schedule,
      nextPayment: schedule.length > 0 ? schedule[0] : null,
      lastPayment: schedule.length > 0 ? schedule[schedule.length - 1] : null
    };
  };

  const metrics = calculatePolicyMetrics();

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FileText },
    { id: 'payments', label: 'Payment Schedule', icon: Calendar },
    { id: 'calculations', label: 'Calculations', icon: TrendingUp },
    { id: 'documents', label: 'Documents', icon: FileText }
  ];

  return (
    <div className="min-h-screen bg-quickbooks-gray">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <Link 
                to="/policies"
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-white rounded-lg transition-colors duration-200"
              >
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{policy.policy_number}</h1>
                <p className="text-gray-600">Policy Details</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button className="btn-primary flex items-center space-x-2">
                <Edit3 className="h-4 w-4" />
                <span>Edit</span>
              </button>
            </div>
          </div>
        </div>

        {/* Policy Summary Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="card bg-gradient-to-r from-enpact-red to-enpact-red-dark text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Total Value</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(metrics.totalValue, policy.currency)}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 opacity-80" />
            </div>
          </div>

          <div className="card bg-gradient-to-r from-enpact-red to-enpact-red-dark text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Premium Amount</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(policy.premium_amount, policy.currency)}
                </p>
              </div>
              <DollarSign className="h-8 w-8 opacity-80" />
            </div>
          </div>

          <div className="card bg-gradient-to-r from-enpact-red to-enpact-red-dark text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Total Periods</p>
                <p className="text-2xl font-bold">{policy.total_period}</p>
              </div>
              <Clock className="h-8 w-8 opacity-80" />
            </div>
          </div>

          <div className="card bg-gradient-to-r from-enpact-red to-enpact-red-dark text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Duration</p>
                <p className="text-2xl font-bold">{metrics.duration} {policy.premium_frequency === 'monthly' ? 'months' : 'years'}</p>
              </div>
              <Calendar className="h-8 w-8 opacity-80" />
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="card mb-6">
          <div className="border-b border-quickbooks-border">
            <nav className="flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                      activeTab === tab.id
                        ? 'border-enpact-red text-enpact-red'
                        : 'border-transparent text-quickbooks-gray-dark hover:text-quickbooks-blue hover:border-quickbooks-blue'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="card">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">Policy Overview</h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Policy Number:</span>
                      <span className="font-medium">{policy.policy_number}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Policyholder:</span>
                      <span className="font-medium">{policy.policyholder_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Insured:</span>
                      <span className="font-medium">{policy.insured_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Currency:</span>
                      <span className="font-medium">{policy.currency}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Premium Details</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Premium Amount:</span>
                      <span className="font-medium">{formatCurrency(policy.premium_amount, policy.currency)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Frequency:</span>
                      <span className="font-medium capitalize">{policy.premium_frequency}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Periods:</span>
                      <span className="font-medium">{policy.total_period}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Value:</span>
                      <span className="font-medium">{formatCurrency(metrics.totalValue, policy.currency)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Timeline</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Effective Start:</span>
                      <span className="font-medium">{formatDate(policy.effective_start_date)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Effective End:</span>
                      <span className="font-medium">{formatDate(policy.effective_end_date)}</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Next Payment:</span>
                      <span className="font-medium">
                        {metrics.nextPayment ? formatDate(metrics.nextPayment.date) : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Last Payment:</span>
                      <span className="font-medium">
                        {metrics.lastPayment ? formatDate(metrics.lastPayment.date) : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'payments' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Payment Schedule</h2>
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-gray-700">View Mode:</span>
                  <div className="flex bg-quickbooks-gray rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('monthly')}
                      className={`px-3 py-1 text-sm font-medium rounded-md transition-colors duration-200 ${
                        viewMode === 'monthly'
                          ? 'bg-enpact-red text-white shadow-quickbooks'
                          : 'text-quickbooks-gray-dark hover:text-enpact-red'
                      }`}
                    >
                      Monthly
                    </button>
                    <button
                      onClick={() => setViewMode('yearly')}
                      className={`px-3 py-1 text-sm font-medium rounded-md transition-colors duration-200 ${
                        viewMode === 'yearly'
                          ? 'bg-enpact-red text-white shadow-quickbooks'
                          : 'text-quickbooks-gray-dark hover:text-enpact-red'
                      }`}
                    >
                      Yearly
                    </button>
                  </div>
                </div>
              </div>

              {metrics.schedule.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-quickbooks-gray border-b border-quickbooks-border">
                        <th className="text-left p-3 font-semibold text-gray-900">Period</th>
                        <th className="text-left p-3 font-semibold text-gray-900">Payment Date</th>
                        <th className="text-left p-3 font-semibold text-gray-900">Amount</th>
                        <th className="text-left p-3 font-semibold text-gray-900">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {metrics.schedule.map((payment, index) => (
                        <tr key={index} className="border-b border-quickbooks-border hover:bg-quickbooks-gray-light">
                          <td className="p-3 font-medium text-gray-900">
                            {payment.period}
                          </td>
                          <td className="p-3 text-gray-700">
                            {formatDate(payment.date)}
                          </td>
                          <td className="p-3 font-semibold text-gray-900">
                            {formatCurrency(payment.amount, payment.currency)}
                          </td>
                          <td className="p-3">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              new Date() > payment.date
                                ? 'bg-green-100 text-green-800'
                                : new Date() < payment.date
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {new Date() > payment.date ? 'Paid' : new Date() < payment.date ? 'Upcoming' : 'Due'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-500 mb-4">
                    <Calendar className="mx-auto h-16 w-16" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Payment Schedule Available</h3>
                  <p className="text-gray-600">Unable to generate payment schedule due to invalid date information.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'calculations' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">Financial Calculations</h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Revenue Projections</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between p-3 bg-quickbooks-gray rounded-lg">
                      <span className="text-gray-600">Monthly Revenue:</span>
                      <span className="font-medium">
                        {policy.premium_frequency === 'monthly' 
                          ? formatCurrency(policy.premium_amount, policy.currency)
                          : formatCurrency(policy.premium_amount / 12, policy.currency)
                        }
                      </span>
                    </div>
                    <div className="flex justify-between p-3 bg-quickbooks-gray rounded-lg">
                      <span className="text-gray-600">Annual Revenue:</span>
                      <span className="font-medium">
                        {policy.premium_frequency === 'yearly' 
                          ? formatCurrency(policy.premium_amount, policy.currency)
                          : formatCurrency(policy.premium_amount * 12, policy.currency)
                        }
                      </span>
                    </div>
                    <div className="flex justify-between p-3 bg-quickbooks-gray rounded-lg">
                      <span className="text-gray-600">Total Contract Value:</span>
                      <span className="font-medium">{formatCurrency(metrics.totalValue, policy.currency)}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Payment Analysis</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between p-3 bg-quickbooks-gray rounded-lg">
                      <span className="text-gray-600">Payments Made:</span>
                      <span className="font-medium">
                        {metrics.schedule.filter(p => new Date() > p.date).length}
                      </span>
                    </div>
                    <div className="flex justify-between p-3 bg-quickbooks-gray rounded-lg">
                      <span className="text-gray-600">Payments Remaining:</span>
                      <span className="font-medium">
                        {metrics.schedule.filter(p => new Date() <= p.date).length}
                      </span>
                    </div>
                    <div className="flex justify-between p-3 bg-quickbooks-gray rounded-lg">
                      <span className="text-gray-600">Completion %:</span>
                      <span className="font-medium">
                        {metrics.schedule.length > 0 
                          ? Math.round((metrics.schedule.filter(p => new Date() > p.date).length / metrics.schedule.length) * 100)
                          : 0}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900">Policy Documents</h2>
              
              <div className="text-center py-12">
                <div className="text-gray-500 mb-4">
                  <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Documents Available</h3>
                <p className="text-gray-600 mb-4">
                  Policy documents will be uploaded and displayed here
                </p>
                <button className="btn-primary flex items-center space-x-2 mx-auto">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span>Upload Document</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PolicyDetailPage;
